import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(
  date: Date | string | number | undefined | null | { toDate?: () => Date; seconds?: number; nanoseconds?: number },
): string {
  if (date == null || date === '') {
    return 'N/A';
  }

  let d: Date;
  if (typeof date === 'string' || typeof date === 'number') {
    d = new Date(date);
  } else if (date instanceof Date) {
    d = date;
  } else if (typeof date.toDate === 'function') {
    d = date.toDate();
  } else if (typeof date.seconds === 'number') {
    const nanos = date.nanoseconds ?? 0;
    d = new Date(date.seconds * 1000 + Math.floor(nanos / 1e6));
  } else {
    return 'Invalid Date';
  }

  // Check if it's a valid date
  if (!(d instanceof Date) || isNaN(d.getTime())) {
    return 'Invalid Date';
  }

  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount);
}

export function calculatePlatformFee(guestCount: number): number {
  return 10 + guestCount * 0.2;
}

export function getDaysDifference(date1: Date, date2: Date): number {
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

export function validatePassword(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Get full name from an object with firstName and lastName fields
 * Falls back to name field if firstName/lastName not available
 */
export function getFullName(
  person: { firstName?: string; lastName?: string; name?: string } | null | undefined
): string {
  if (!person) return '';

  const fromParts = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  if (fromParts) return fromParts;

  return person.name || '';
}

/**
 * Get first name from an object with firstName and name fields
 * Falls back to splitting name if firstName not available
 */
export function getFirstName(
  person: { firstName?: string; name?: string } | null | undefined
): string {
  if (!person) return '';
  
  if (person.firstName) {
    return person.firstName;
  }
  
  if (person.name) {
    return person.name.split(' ')[0] || '';
  }
  
  return '';
}

/**
 * Get last name from an object with lastName and name fields
 * Falls back to splitting name if lastName not available
 */
export function getLastName(
  person: { lastName?: string; name?: string } | null | undefined
): string {
  if (!person) return '';
  
  if (person.lastName) {
    return person.lastName;
  }
  
  if (person.name) {
    const parts = person.name.split(' ');
    return parts.slice(1).join(' ') || '';
  }
  
  return '';
}

